<template>
    <!-- "Who signs this lease?" for a lease the landlord uploaded themselves
         (RAC-10257). Modelled on DocumentSignerStep, but that component is a
         FIXED roster: a generated document's signature blocks come from its
         template, so the landlord fills them in and can neither add nor remove
         one. An uploaded PDF has no template behind it, so this roster is
         authored from scratch — hence add/remove, and the landlord row pinned
         first because RentRisk holds their countersign back until every tenant
         has signed. -->
    <div class="bg-white p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-1">Who signs this lease?</h3>
        <p class="text-sm text-gray-500 mb-4">
            Add everyone who needs to sign. Tenants are emailed their signing link first; you countersign
            once they have all signed. Everyone needs a different email address.
        </p>

        <form class="space-y-4" @submit.prevent="handleSubmit">
            <fieldset
                v-for="(signer, idx) in signerRows"
                :key="signer.key"
                class="border border-gray-200 rounded-lg p-4 relative"
            >
                <legend class="text-sm font-medium text-gray-800 px-1">{{ roleLabel(signer, idx) }}</legend>

                <!-- The landlord row has no Remove: exactly one landlord is
                     required, and they are the row whose countersign is held
                     back until every tenant has signed. -->
                <button
                    v-if="signer.role === 'tenant' && tenantCount > 1"
                    type="button"
                    class="absolute top-2 right-2 text-xs text-red-600 hover:text-red-800"
                    @click="removeTenant(idx)"
                >
                    Remove
                </button>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label :for="`uploaded-signer-name-${signer.key}`" class="block text-sm text-gray-700 mb-1">
                            Full name <span class="text-red-500">*</span>
                        </label>
                        <input
                            :id="`uploaded-signer-name-${signer.key}`"
                            v-model="signer.name"
                            type="text"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                        <p v-if="errors[idx]?.name" class="text-xs text-red-600 mt-1">{{ errors[idx].name }}</p>
                    </div>

                    <div>
                        <label :for="`uploaded-signer-email-${signer.key}`" class="block text-sm text-gray-700 mb-1">
                            Email <span class="text-red-500">*</span>
                        </label>
                        <input
                            :id="`uploaded-signer-email-${signer.key}`"
                            v-model="signer.email"
                            type="email"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                        <p v-if="errors[idx]?.email" class="text-xs text-red-600 mt-1">{{ errors[idx].email }}</p>
                    </div>
                </div>
            </fieldset>

            <button
                v-if="canAddTenant"
                type="button"
                class="text-sm font-medium text-blue-600 hover:text-blue-700"
                @click="addTenant"
            >
                + Add another tenant
            </button>
            <p v-else class="text-xs text-gray-500">You can add up to {{ MAX_TENANTS }} tenants.</p>

            <p v-if="formError" class="text-sm text-red-600 bg-red-50 p-2 rounded">{{ formError }}</p>
            <p v-if="serverError" class="text-sm text-red-600 bg-red-50 p-2 rounded">{{ serverError }}</p>

            <div class="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-100">
                <button type="button" class="closeButton" @click="$emit('cancel')">Cancel</button>
                <button type="submit" class="primaryButton" :disabled="busy" :class="{ 'opacity-25': busy }">
                    {{ busy ? busyLabel : 'Continue' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'

// Matches PrepareUploadedLeaseRequest's cap of 7 signers (landlord + 6 tenants),
// itself matched to the generated lease's UpdateLeaseSignersRequest.
const MAX_TENANTS = 6

const props = defineProps({
    // Prefills the landlord row — they are the one sending, so it is nearly
    // always them.
    landlordName: { type: String, default: '' },
    landlordEmail: { type: String, default: '' },
    busy: { type: Boolean, default: false },
    busyLabel: { type: String, default: 'Setting up…' },
    serverError: { type: String, default: null },
})

const emit = defineEmits(['submit', 'cancel'])

let nextKey = 0
const newKey = () => `signer-${nextKey++}`

const signerRows = reactive([
    { key: newKey(), role: 'landlord', name: props.landlordName || '', email: props.landlordEmail || '' },
    { key: newKey(), role: 'tenant', name: '', email: '' },
])

const errors = ref([])
const formError = ref(null)

const tenantCount = computed(() => signerRows.filter((signer) => signer.role === 'tenant').length)
const canAddTenant = computed(() => tenantCount.value < MAX_TENANTS)

const roleLabel = (signer, idx) => {
    if (signer.role === 'landlord') {
        return 'Landlord (you countersign last)'
    }

    return tenantCount.value > 1 ? `Tenant ${tenantIndex(idx)}` : 'Tenant'
}

// Position among tenants, not among all rows — the landlord occupies row 0.
const tenantIndex = (idx) => signerRows.slice(0, idx + 1).filter((signer) => signer.role === 'tenant').length

const addTenant = () => {
    if (!canAddTenant.value) {
        return
    }

    signerRows.push({ key: newKey(), role: 'tenant', name: '', email: '' })
}

const removeTenant = (idx) => {
    if (signerRows[idx]?.role !== 'tenant') {
        return
    }

    signerRows.splice(idx, 1)
    errors.value = []
}

const validate = () => {
    const found = []
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const seen = new Map()

    formError.value = null

    signerRows.forEach((signer, idx) => {
        const rowErrors = {}

        if (!signer.name.trim()) {
            rowErrors.name = 'Name is required — it identifies this signer during signing.'
        }

        const email = signer.email.trim().toLowerCase()
        if (!email) {
            rowErrors.email = 'Email is required — this person signs electronically.'
        } else if (!emailRe.test(email)) {
            rowErrors.email = 'Enter a valid email address.'
        } else if (seen.has(email)) {
            // RocketLawyer rejects a binder whose parties share an address, and
            // catching it here beats surfacing its raw rejection after the send.
            rowErrors.email = 'Each signer needs a different email address.'
        } else {
            seen.set(email, idx)
        }

        if (Object.keys(rowErrors).length > 0) {
            found[idx] = rowErrors
        }
    })

    errors.value = found

    if (tenantCount.value < 1) {
        formError.value = 'Add at least one tenant to sign this lease.'

        return false
    }

    return found.length === 0
}

const handleSubmit = () => {
    if (!validate()) {
        return
    }

    emit(
        'submit',
        signerRows.map((signer) => ({
            role: signer.role,
            name: signer.name.trim(),
            email: signer.email.trim(),
        })),
    )
}
</script>
